import React, { useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { showPopup } from '../../actions/uiActions'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SettingsIcon from '@mui/icons-material/Settings'
import WorkIcon from '@mui/icons-material/Work'
import HistoryIcon from '@mui/icons-material/History'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import IconButton from '@mui/material/IconButton'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PlayDisabledIcon from '@mui/icons-material/PlayDisabled'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Collapse from '@mui/material/Collapse'
import Box from '@mui/material/Box'
import NewWizardDialog from '../ui/tasks/createWizard/NewWizardDialog'
import { dateParser } from '../utilities/helperFunctions'

// ─── Constants ───────────────────────────────────────────────────────────────
const SSIS_JOB_TYPE = 1
const DB_JOB_TYPE = 2
const ENTRIES_PER_PAGE = 5

// ─── Helper: collect file names referenced by a job ──────────────────────────
function getFileNamesUsedByJobs(dbJobs) {
    const VALUE = 'Value'
    let filesBeingUsed = []
    for (let jobIterator = 0; jobIterator < dbJobs.length; jobIterator++) {
        let jobData = JSON.parse(dbJobs[jobIterator]['jobData'])
        let jobTasks = jobData['Tasks']
        for (let taskIterator = 0; taskIterator < jobTasks.length; taskIterator++) {
            const t = jobTasks[taskIterator][VALUE]
            if (t.selectedZipFile?.trim()) filesBeingUsed.push(t.selectedZipFile)
            if (t.selectedFileWithinZip?.trim()) filesBeingUsed.push(t.selectedFileWithinZip)
            if (t.wizardFile?.trim()) filesBeingUsed.push(t.wizardFile)
            if (t.sourceFiles?.length) filesBeingUsed.push(...t.sourceFiles)
        }
    }
    return filesBeingUsed
}

function getFilesForJob(files, job) {
    if (!job) return files
    let allCurrentFileNames = []
    const selectedJobInfo = JSON.parse(job['jobData'])
    const selectedJobTasks = selectedJobInfo['Tasks']
    for (let taskItr = 0; taskItr < selectedJobTasks.length; taskItr++) {
        const currentTaskValue = selectedJobTasks[taskItr]['Value']
        const sourceFiles = currentTaskValue['sourceFiles'] || []
        if (currentTaskValue['wizardFile'] && currentTaskValue['wizardFile'] !== '') {
            allCurrentFileNames.push(currentTaskValue['wizardFile'])
        }
        if (currentTaskValue['selectedZipFileId'] && currentTaskValue['selectedZipFileId'] !== '') {
            allCurrentFileNames.push(currentTaskValue['selectedZipFileId'])
        }
        for (let sourceItr = 0; sourceItr < sourceFiles.length; sourceItr++) {
            allCurrentFileNames.push(sourceFiles[sourceItr])
        }
    }
    const nameSet = new Set(allCurrentFileNames)
    return files.filter(file => {
        const data = JSON.parse(file.jobFileData)
        return nameSet.has(data.identifier)
    })
}

function jobOnDbIsRunning(currentJob, dbJobs, dbHistoryEntries) {
    let dbRunningAgainst = ''
    dbJobs.forEach(j => {
        dbHistoryEntries.forEach(history => {
            if (history['dbJobId'] == j['id'] && history['jobState'] === 'Running') {
                dbRunningAgainst = j['database']
            }
        })
    })
    if (dbRunningAgainst === '') return false
    return dbHistoryEntries.some(history =>
        (history['dbJobId'] == currentJob['id'] && history['jobState'] === 'Running') ||
        (dbRunningAgainst === currentJob['database'])
    )
}
// ─── Sub-component: expandable section row ───────────────────────────────────
function SectionRow({ icon, label, count, isOpen, onToggle }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
            onClick={onToggle}>
            {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            {icon}
            <Typography>{label}</Typography>
            <Typography>({count})</Typography>
        </Box>
    )
}

// ─── Sub-component: Jobs section ─────────────────────────────────────────────
// ─── Sub-component: Jobs section ─────────────────────────────────────────────
function ProjectJobsSection({ jobs, allGlobalFiles, dbHistoryEntries, role, sqLDbList, ssisJobs, performingJob, jobProgressId, currentJobProgress, currentJobTotalParts, showPopup, downloadFile, setWizardState }) {
    const [page, setPage] = useState(1)
    const isAdminRole = role === 'IEAdmin' || role === 'ClientAdmin'
    const totalPages = Math.ceil(jobs.length / ENTRIES_PER_PAGE) || 1

    if (jobs.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ pl: 4, py: 0.5 }}>No jobs.</Typography>
    }

    return (
        <Box sx={{ pl: 8 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Job Name</TableCell>
                        <TableCell>Job Description</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {jobs.map((job, i) => {
                        const min = (page - 1) * ENTRIES_PER_PAGE
                        const max = min + ENTRIES_PER_PAGE - 1
                        if (i < min || i > max) return null
                        const isRunning = jobOnDbIsRunning(job, jobs, dbHistoryEntries)
                        return (
                            <TableRow key={job.id}>
                                <TableCell>{job.jobName}</TableCell>
                                <TableCell>{job.jobDescription}</TableCell>
                                <TableCell>
                                    <Tooltip title={isRunning ? 'Job is running' : 'Run Job'}>
                                        <span>
                                            <IconButton size="small" disabled={isRunning}
                                                onClick={() => !isRunning && showPopup('fireDbJob', { jobData: job })}>
                                                {isRunning ? <PlayDisabledIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {isAdminRole && (
                                        <Tooltip title="Edit Job">
                                            <IconButton size="small" onClick={() => showPopup('dataToolsJobTaskList', { jobData: job, databases: sqLDbList, packages: ssisJobs, globalFiles: allGlobalFiles })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}                                  
                                    {isAdminRole && (
                                        <Tooltip title="Move to another project">
                                            <IconButton size="small" onClick={() => showPopup('moveDbJobToProject', { jobData: job })}>
                                                <DriveFileMoveIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {isAdminRole && (
                                        <Tooltip title="Delete Job">
                                            <IconButton size="small" onClick={() => showPopup('deleteDbJob', { jobId: job.id })}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            {totalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Pagination count={totalPages} size="small" color="primary" page={page} onChange={(e, p) => { e.preventDefault(); setPage(p) }} />
                </Stack>
            )}
        </Box>
    )
}

// ─── Sub-component: Files section ────────────────────────────────────────────
function ProjectFilesSection({ files, dbJobs, role, showPopup, downloadFile, globalFiles, setWizardState }) {
    const [page, setPage] = useState(1)
    const isAdminRole = role === 'IEAdmin' || role === 'ClientAdmin'
    const totalPages = Math.ceil(files.length / ENTRIES_PER_PAGE) || 1

    if (files.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ pl: 3, py: 0.5 }}>No files.</Typography>
    }

    return (
        <Box sx={{ pl: 8 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>File Name</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {files.map((x, i) => {
                        const min = (page - 1) * ENTRIES_PER_PAGE
                        const max = min + ENTRIES_PER_PAGE - 1
                        if (i < min || i > max) return null
                        return (
                            <TableRow key={x.id}>
                                <TableCell>{x.fileName}</TableCell>
                                <TableCell>{x.createdUserName}</TableCell>
                                <TableCell>{dateParser(x.updatedDate ?? x.createdDate)}</TableCell>
                                <TableCell>
                                    <Tooltip title="View Inside">
                                        <IconButton size="small" onClick={() => showPopup('lookInsideZipFile', { fileData: x.jobFileData })}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => {
                                            if (isAdminRole && x['fileName'].toLowerCase().endsWith('.json')) {
                                                setWizardState(x)
                                            } else {
                                                showPopup('databaseJobFileUpload', { jobData: x, existingFiles: globalFiles })
                                            }
                                        }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => {
                                            const filesBeingUsed = getFileNamesUsedByJobs(dbJobs)
                                            const jobFileData = JSON.parse(x['jobFileData'])
                                            const extra = isAdminRole ? { targetFileId: jobFileData['identifier'] } : {}
                                            showPopup('deleteDbJobFile', { fileId: x.id, filesBeingUsed, targetFile: x['fileName'], ...extra })
                                        }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                        <IconButton size="small" onClick={() => downloadFile(x.id, x.fileName)}>
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            {totalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Pagination count={totalPages} size="small" color="primary" page={page} onChange={(e, p) => { e.preventDefault(); setPage(p) }} />
                </Stack>
            )}
        </Box>
    )
}

// ─── Sub-component: Logs section ─────────────────────────────────────────────
function ProjectLogsSection({ histories, jobs, showPopup, performingJob, jobProgressId, currentJobProgress, currentJobTotalParts }) {
    const [page, setPage] = useState(1)
    const totalPages = Math.ceil(histories.length / ENTRIES_PER_PAGE) || 1

    const jobNameMap = useMemo(() => {
        const map = {}
        jobs.forEach(j => { map[j.id] = j.jobName })
        return map
    }, [jobs])

    if (histories.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ pl: 3, py: 0.5 }}>No logs.</Typography>
    }

    return (
        <Box sx={{ pl: 8 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Job Name</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {histories.map((x, i) => {
                        const min = (page - 1) * ENTRIES_PER_PAGE
                        const max = min + ENTRIES_PER_PAGE - 1
                        if (i < min || i > max) return null
                        return (
                            <TableRow key={x.id}>
                                <TableCell>{jobNameMap[parseInt(x.dbJobId)] ?? x.dbJobId}</TableCell>
                                <TableCell>{dateParser(x.startDate)}</TableCell>
                                <TableCell>{dateParser(x.stopDate)}</TableCell>
                                <TableCell>{x.userName}</TableCell>
                                <TableCell>{x.jobState}</TableCell>
                                <TableCell>
                                    <Tooltip title="View Log">
                                        <IconButton size="small" onClick={() => showPopup('viewDbJobLog', { historyData: x })}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {performingJob && parseInt(x.id) === parseInt(jobProgressId) && (
                                        <div className="progressBar" style={{ width: '100%' }}>
                                            <div className="filler" style={{ width: `${(currentJobProgress / currentJobTotalParts) * 100}%` }} />
                                        </div>
                                    )}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            {totalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Pagination count={totalPages} size="small" color="primary" page={page} onChange={(e, p) => { e.preventDefault(); setPage(p) }} />
                </Stack>
            )}
        </Box>
    )
}
// ─── Sub-component: Project node ─────────────────────────────────────────────
function ProjectNode({ project, jobs, allGlobalFiles, dbHistoryEntries, role, sqLDbList, ssisJobs, performingJob, jobProgressId, currentJobProgress, currentJobTotalParts, showPopup, downloadFile, setWizardState }) {
    const [open, setOpen] = useState(false)
    const [openSections, setOpenSections] = useState(new Set())

    const projectJobIds = useMemo(() => new Set(jobs.map(j => j.id)), [jobs])

    const projectHistories = useMemo(() =>
        dbHistoryEntries.filter(h => projectJobIds.has(parseInt(h.dbJobId)))
        , [dbHistoryEntries, projectJobIds])

    const projectFiles = useMemo(() =>
        allGlobalFiles.filter(f => f.projectId === project.projectId)
        , [allGlobalFiles, project.projectId])

    function toggleSection(section) {
        setOpenSections(prev => {
            const next = new Set(prev)
            if (next.has(section)) {
                next.delete(section)
            } else {
                next.add(section)
            }
            return next
        })
    }

    return (
        <Box sx={{ mb: 1 }}>
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: 1,
                    cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: open ? 'action.selected' : 'transparent'
                }}
                onClick={() => setOpen(o => !o)}
            >
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                <WorkIcon fontSize="small" color="primary" />
                <Typography>
                    {project.projectName}
                </Typography>
                <Typography>
                    ({jobs.length} job{jobs.length !== 1 ? 's' : ''}, {projectFiles.length} file{projectFiles.length !== 1 ? 's' : ''})
                </Typography>
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 3, borderLeft: '2px solid', borderColor: 'divider', ml: 2, mt: 0.5 }}>

                    <SectionRow
                        icon={<SettingsIcon fontSize="small" color="action" />}
                        label="Jobs"
                        count={jobs.length}
                        isOpen={openSections.has('jobs')}
                        onToggle={() => toggleSection('jobs')}
                    />
                    <Collapse in={openSections.has('jobs')} timeout="auto" unmountOnExit>
                        <ProjectJobsSection
                            jobs={jobs}
                            allGlobalFiles={allGlobalFiles}
                            dbHistoryEntries={dbHistoryEntries}
                            role={role}
                            sqLDbList={sqLDbList}
                            ssisJobs={ssisJobs}
                            performingJob={performingJob}
                            jobProgressId={jobProgressId}
                            currentJobProgress={currentJobProgress}
                            currentJobTotalParts={currentJobTotalParts}
                            showPopup={showPopup}
                            downloadFile={downloadFile}
                            setWizardState={setWizardState}
                        />
                    </Collapse>

                    <SectionRow
                        icon={<InsertDriveFileIcon fontSize="small" color="action" />}
                        label="Files"
                        count={projectFiles.length}
                        isOpen={openSections.has('files')}
                        onToggle={() => toggleSection('files')}
                    />
                    <Collapse in={openSections.has('files')} timeout="auto" unmountOnExit>
                        <ProjectFilesSection
                            files={projectFiles}
                            dbJobs={jobs}
                            role={role}
                            showPopup={showPopup}
                            downloadFile={downloadFile}
                            globalFiles={allGlobalFiles}
                            setWizardState={setWizardState}
                        />
                    </Collapse>

                    <SectionRow
                        icon={<HistoryIcon fontSize="small" color="action" />}
                        label="Logs"
                        count={projectHistories.length}
                        isOpen={openSections.has('logs')}
                        onToggle={() => toggleSection('logs')}
                    />
                    <Collapse in={openSections.has('logs')} timeout="auto" unmountOnExit>
                        <ProjectLogsSection
                            histories={projectHistories}
                            jobs={jobs}
                            showPopup={showPopup}
                            performingJob={performingJob}
                            jobProgressId={jobProgressId}
                            currentJobProgress={currentJobProgress}
                            currentJobTotalParts={currentJobTotalParts}
                        />
                    </Collapse>

                </Box>
            </Collapse>
        </Box>
    )
}

// ─── Main Component ───────────────────────────────────────────────────────────
function TabDataTools(props) {
    const [projectsPage, setProjectsPage] = useState(1)

    const [wizardProjectId, setWizardProjectId] = useState(-1)
    const [wizardStep, setWizardStep] = useState('')
    const [headerRow, setHeaderRow] = useState(0)
    const [factSourceFirstRow, setFactSourceFirstRow] = useState(-1)
    const [tableDefinition, setTableDefinition] = useState({ tableHasHeader: false, namedColumns: false })
    const [factsColumnRanges, setFactsColumnRanges] = useState([])
    const [dimensions, setDimensions] = useState([])
    const [aggregationSelections, setAggregationSelections] = useState([])
    const [selectedDimension, setSelectedDimension] = useState({ id: -1, name: '', outputFormat: [], ranges: [], segments: [], type: 1 })
    const [wizardFileName, setWizardFileName] = useState('')
    const [wizardFileId, setWizardFileId] = useState(-1)

    const dbJobs = useMemo(() => props.jobs.filter(x => x.jobType === DB_JOB_TYPE), [props.jobs])
    const ssisJobs = useMemo(() => props.jobs.filter(x => x.jobType === SSIS_JOB_TYPE), [props.jobs])

    const dbHistoryEntries = useMemo(() => {
        const jobIds = new Set(dbJobs.map(j => j.id))
        return props.histories.filter(h => jobIds.has(parseInt(h.dbJobId)))
    }, [props.histories, dbJobs])

    const projectGroups = useMemo(() => {
        const groups = {}

        dbJobs.forEach(job => {
            const pid = job.projectId ?? 0
            if (!groups[pid]) {
                groups[pid] = {
                    projectId: pid,
                    projectName: job.project?.projectName || `Project ${pid}`,
                    jobs: []
                }
            }
            groups[pid].jobs.push(job)
        })

        props.globalFiles.forEach(file => {
            const pid = file.projectId ?? 0
            if (!groups[pid]) {
                groups[pid] = {
                    projectId: pid,
                    projectName: file.project?.projectName || `Project ${pid}`,
                    jobs: []
                }
            }
        })

        return Object.values(groups).sort((a, b) => a.projectId - b.projectId)
    }, [dbJobs, props.globalFiles])

    const totalProjectPages = Math.ceil(projectGroups.length / ENTRIES_PER_PAGE) || 1
    const visibleProjects = projectGroups.slice((projectsPage - 1) * ENTRIES_PER_PAGE, projectsPage * ENTRIES_PER_PAGE)

    function resetWizard() {
        setWizardProjectId(-1)
        setHeaderRow(0)
        setFactSourceFirstRow(-1)
        setTableDefinition({ tableHasHeader: false, namedColumns: false })
        setFactsColumnRanges([])
        setDimensions([])
        setAggregationSelections([])
        setWizardFileId(-1)
        setWizardFileName('')
        setWizardStep('')
    }

    function openNewWizard() {
        resetWizard()
        setWizardStep('select-files')
    }

    function setWizardStateFromFile(x) {
        setWizardFileName(x['fileName'])
        setWizardStep('loading-wizard-file')
        setWizardFileId(x['id'])
        props.openEditNewWizardFile(x['id'])
            .then(resp => {
                if (resp.status !== 200) throw new Error()
                return resp.json()
            })
            .then(json => {
                console.log("tools", json.projectId)
                setWizardProjectId(json.projectId)
                setTableDefinition(json['tableDefinition'])
                setHeaderRow(json['headerRow'])
                setFactSourceFirstRow(json['headerRow'])
                setFactsColumnRanges(json['factsColumnRanges'])
                setDimensions(json['dimensions'])
                setAggregationSelections(json['aggregationSelections'])
                setSelectedDimension(json['selectedDimension'])
                setWizardStep('select-files')
            })
            .catch(ex => {
                console.error(ex)
                resetWizard()
            })
    }

    return (
        <>
            <div className="callsToAction" style={{ margin: '10px 0px', width: '95%', padding: '10px' }}>
                <button type="submit" className="btn" onClick={e => { e.preventDefault(); props.showPopup('dataToolsJobTaskList', { jobData: null, databases: props.dbList, packages: ssisJobs, globalFiles: props.globalFiles }) }}>
                    Add New Job
                </button>&nbsp;
                <button type="submit" className="btn" onClick={e => { e.preventDefault(); props.showPopup('databaseJobFileUpload', { jobData: null, existingFiles: props.globalFiles }) }}>
                    Add New Global File
                </button>&nbsp;
                <button type="submit" className="btn" onClick={e => { e.preventDefault(); openNewWizard() }}>
                    Add New Wizard File
                </button>&nbsp;
                <button type="submit" className="btn" onClick={e => { e.preventDefault(); props.showPopup('addEditProject') }}>
                    Manage Projects
                </button>
            </div>

            <Paper elevation={3} className="dbFunctionsPaper">
                {projectGroups.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No jobs found. Use <strong>Add New Job</strong> to get started.
                    </Typography>
                )}

                {visibleProjects.map(project => (
                    <ProjectNode
                        key={project.projectId}
                        project={project}
                        jobs={project.jobs}
                        allGlobalFiles={props.globalFiles}
                        dbHistoryEntries={dbHistoryEntries}
                        role={props.role}
                        sqLDbList={props.dbList}
                        ssisJobs={ssisJobs}
                        performingJob={props.performingJob}
                        jobProgressId={props.jobProgressId}
                        currentJobProgress={props.currentJobProgress}
                        currentJobTotalParts={props.currentJobTotalParts}
                        showPopup={props.showPopup}
                        downloadFile={props.downloadFile}
                        setWizardState={setWizardStateFromFile}
                    />
                ))}

                {totalProjectPages > 1 && (
                    <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                        <Pagination
                            count={totalProjectPages}
                            color="primary"
                            page={projectsPage}
                            onChange={(e, newPage) => { e.preventDefault(); setProjectsPage(newPage) }}
                        />
                    </Stack>
                )}
            </Paper>

            <NewWizardDialog
                projectId={wizardProjectId}
                wizardStep={wizardStep}
                globalFiles={props.globalFiles}
                tableDefinition={tableDefinition}
                headerRow={headerRow}
                factSourceFirstRow={factSourceFirstRow}
                factsColumnRanges={factsColumnRanges}
                dimensions={dimensions}
                aggregationSelections={aggregationSelections}
                selectedDimension={selectedDimension}
                wizardFileName={wizardFileName}
                wizardFileId={wizardFileId}
                onClose={resetWizard}
            />
        </>
    )
}

function mapStateToProps(state) {
    return {
        jobs: state.selfServeJobs.jobs,
        histories: state.selfServeJobs.histories,
        globalFiles: state.selfServeJobs.globalFiles,
        performingJob: state.selfServeJobs.performingJob,
        jobProgressId: state.selfServeJobs.jobProgressId,
        currentJobProgress: state.selfServeJobs.currentJobProgress,
        currentJobTotalParts: state.selfServeJobs.currentJobTotalParts,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators({ showPopup }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(TabDataTools)